
"use client";

import Link from "next/link";
import dynamic from "next/dynamic";
import React, { useEffect, useMemo, useState, useRef, useId } from "react";
import { components } from "react-select";
import type { InputProps, GroupBase } from "react-select";

const ReactSelect = dynamic(() => import("react-select"), { ssr: false });
import { motion } from "framer-motion";
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import {
    pageVariants,
    formItemVariants,
    buttonVariants,
    slideFromRightVariants,
    listItemVariants,
    headerVariants,
    staggerContainerVariants
} from "@/lib/animations";
import { getLanguageFromPathname } from "@/lib/language-detection";
import type { ContactInfo } from "@/lib/server/contact";
import { useLanguageVariables } from "@/contexts/LanguageVariablesContext";
import { resolveUrlFromMap, buildLocalizedLink } from "@/lib/blog-transformers";
import type { PageUrlMap } from "@/types/blog";
import { sanitizeCmsHtml } from "@/lib/sanitize-cms-html";
import AgreementModal from "@/components/ui/AgreementModal";
import { splitAgreementTemplate } from "@/lib/agreement-text";
import {
    loadRecaptchaScript, renderRecaptchaWidget, resetRecaptchaWidget, getRecaptchaToken,
    loadRecaptchaV3Script, getRecaptchaV3Token,
    loadTurnstileScript, renderTurnstileWidget, resetTurnstileWidget, getTurnstileToken, removeTurnstileWidget
} from "@/lib/captcha";
import { useCaptchaConfig } from "@/hooks/useCaptchaConfig";
import { useCountries } from "@/contexts/CountriesContext";

const motionItemProps = {
    initial: "hidden" as const,
    animate: "visible" as const,
};

interface AgreementItem {
    id: number;
    title: string;
    slug: string;
    content?: string;
    is_required: boolean;
}

interface FormAgreements {
    required: AgreementItem[];
    optional: AgreementItem[];
}

interface FormDetails {
    id: number;
    title: string;
    slug: string;
    language: {
        id?: number | null;
        name?: string | null;
        code?: string | null;
    };
    agreements: FormAgreements;
    fields?: Array<{
        type?: string;
        name?: string;
        field?: string;
        label?: string;
        title?: string;
        placeholder?: string;
        required?: boolean | string | number;
        rows?: number;
        options?: string[] | Array<{ value: string; label: string }>;
    }>;
    updated_at?: string | null;
}

interface ContactFormConfig {
    id?: number;
    slug?: string;
    title?: string;
    languageId?: number;
    language?: {
        name?: string | null;
        code?: string | null;
    };
}

const searchableSelectStyles = {
    control: (base: Record<string, unknown>, state: { isFocused?: boolean }) => ({
        ...base,
        borderColor: state.isFocused ? "rgba(227, 30, 36, 0.4)" : "rgba(0,0,0,0.08)",
        borderRadius: "0.5rem",
        minHeight: "52px",
        boxShadow: "none",
        fontSize: "0.9375rem",
        "&:hover": { borderColor: "rgba(0,0,0,0.15)" },
    }),
    option: (base: Record<string, unknown>, state: { isSelected?: boolean; isFocused?: boolean }) => ({
        ...base,
        backgroundColor: state.isSelected ? "#E31E24" : state.isFocused ? "#fef2f2" : "white",
        color: state.isSelected ? "white" : "#4F545A",
        fontSize: "0.9375rem",
        padding: "10px 16px",
    }),
    placeholder: (base: Record<string, unknown>) => ({ ...base, color: "#9CA3AF" }),
    singleValue: (base: Record<string, unknown>) => ({ ...base, color: "#4F545A" }),
    menu: (base: Record<string, unknown>) => ({ ...base, zIndex: 9999 }),
    indicatorSeparator: () => ({ display: "none" as const }),
};

const NoAutocompleteInput = (props: InputProps<unknown, false, GroupBase<unknown>>) => {
    const uniqueId = useId();
    const { onFocus, readOnly: _ro, ...rest } = props;
    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        e.target.removeAttribute("readonly");
        onFocus?.(e);
    };
    return (
        <components.Input
            {...rest}
            onFocus={handleFocus}
            autoComplete="nope"
            aria-autocomplete="list"
            data-lpignore="true"
            data-form-type="other"
            name={`contact-form-select-${uniqueId.replace(/:/g, "")}`}
            readOnly
        />
    );
};

const CONTACT_TITLE_VALUES = ["Mr", "Mrs", "Ms", "Miss", "Dr", "Other"] as const;

type ContactFormSelectOption = { value: string; label: string };

const phoneSvg = '<svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg"> <path fill-rule="evenodd" clip-rule="evenodd" d="M3.81353 4.748C3.81619 4.748 3.74953 4.81733 3.66553 4.9C3.34286 5.21333 2.67486 5.86533 2.66813 7.22933C2.65753 9.13733 3.91219 12.68 9.95353 18.72C15.9669 24.7307 19.5042 26 21.4162 26H21.4442C22.8082 25.9933 23.4589 25.324 23.7735 25.0027C23.8695 24.904 23.9469 24.832 24.0015 24.7867C25.3295 23.4507 26.0095 22.456 26.0029 21.8187C25.9935 21.168 25.1842 20.3987 24.0655 19.3347C23.7095 18.996 23.3229 18.628 22.9149 18.22C21.8575 17.1653 21.3335 17.3453 20.1815 17.7507C18.5882 18.3093 16.4015 19.0693 13.0042 15.6707C9.60153 12.2707 10.3629 10.0867 10.9202 8.49333C11.3229 7.34133 11.5069 6.816 10.4482 5.75733C10.0335 5.344 9.66153 4.952 9.31886 4.592C8.26153 3.48 7.49886 2.676 6.85219 2.66666H6.84153C6.20286 2.66666 5.21086 3.34933 3.80686 4.75333C3.81086 4.74933 3.81219 4.748 3.81353 4.748ZM21.4175 28C18.0949 28 13.7629 25.3547 8.54019 20.1347C3.29753 14.8933 0.648192 10.548 0.66808 7.21866C0.680192 5.01866 1.83353 3.892 2.26686 3.46933C2.28953 3.44133 2.36686 3.36533 2.39353 3.33866C4.30553 1.42533 5.59486 0.651996 6.87753 0.666459C8.36686 0.686663 9.42686 1.80133 10.7682 3.21333C11.1002 3.56266 11.4602 3.94266 11.8615 4.34266C13.8082 6.28933 13.2535 7.87733 12.8082 9.152C12.3229 10.5427 11.9029 11.7427 14.4175 14.2573C16.9349 16.772 18.1349 16.352 19.5202 15.8627C20.7962 15.4173 22.3802 14.86 24.3295 16.8067C24.7242 17.2013 25.0989 17.5573 25.4442 17.8867C26.8629 19.2347 27.9829 20.3 28.0018 21.7933C28.0175 23.0667 27.2442 24.364 25.3349 26.2747L24.4895 25.7013L25.2042 26.4C24.7815 26.8333 23.6562 27.988 21.4549 28H21.4175Z" fill="#1A1A1A"/> </svg>';
const emailSvg = '<svg width="29" height="27" viewBox="0 0 29 27" fill="none" xmlns="http://www.w3.org/2000/svg"> <path fill-rule="evenodd" clip-rule="evenodd" d="M21.2107 0.667236C25.6507 0.667236 28.9987 4.2899 28.9987 9.0939V18.2512C28.9987 20.7099 28.1294 22.9312 26.5494 24.5072C25.132 25.9192 23.2934 26.6672 21.232 26.6672H8.0947C6.03736 26.6672 4.20003 25.9206 2.78136 24.5072C1.20136 22.9312 0.332031 20.7099 0.332031 18.2512V9.0939C0.332031 4.2899 3.68003 0.667236 8.12003 0.667236H21.2107ZM21.2107 2.66724H8.12003C4.7667 2.66724 2.33203 5.3699 2.33203 9.0939V18.2512C2.33203 20.1752 2.99336 21.8939 4.19336 23.0899C5.22803 24.1232 6.5787 24.6672 8.0987 24.6672H21.2107C21.2134 24.6646 21.224 24.6672 21.232 24.6672C22.7534 24.6672 24.1027 24.1232 25.1374 23.0899C26.3387 21.8939 26.9987 20.1752 26.9987 18.2512V9.0939C26.9987 5.3699 24.564 2.66724 21.2107 2.66724ZM23.312 8.83897C23.66 9.26697 23.5947 9.8963 23.1667 10.2456L17.2414 15.0616C16.492 15.6563 15.596 15.9536 14.7014 15.9536C13.8094 15.9536 12.92 15.659 12.176 15.0696L6.19603 10.2483C5.76536 9.90164 5.6987 9.27097 6.04403 8.84164C6.39203 8.41364 7.02136 8.34564 7.4507 8.69097L13.4254 13.507C14.176 14.1016 15.2334 14.1016 15.9894 13.5016L21.904 8.69364C22.3334 8.34297 22.9627 8.40964 23.312 8.83897Z" fill="#1A1A1A"/> </svg>';
const addressSvg = '<svg width="23" height="27" viewBox="0 0 23 27" fill="none" xmlns="http://www.w3.org/2000/svg"> <path fill-rule="evenodd" clip-rule="evenodd" d="M11.332 0.667236C17.3974 0.667236 22.332 5.63924 22.332 11.7512C22.332 19.2646 13.724 26.6672 11.332 26.6672C8.94003 26.6672 0.332031 19.2646 0.332031 11.7512C0.332031 5.63924 5.2667 0.667236 11.332 0.667236ZM11.332 2.66724C6.36936 2.66724 2.33203 6.74324 2.33203 11.7512C2.33203 18.1232 9.8307 24.3312 11.332 24.6619C12.8334 24.3299 20.332 18.1219 20.332 11.7512C20.332 6.74324 16.2947 2.66724 11.332 2.66724ZM11.3334 7.3339C13.7227 7.3339 15.6667 9.2779 15.6667 11.6686C15.6667 14.0579 13.7227 16.0006 11.3334 16.0006C8.94403 16.0006 7.00003 14.0579 7.00003 11.6686C7.00003 9.2779 8.94403 7.3339 11.3334 7.3339ZM11.3334 9.3339C10.0467 9.3339 9.00003 10.3806 9.00003 11.6686C9.00003 12.9552 10.0467 14.0006 11.3334 14.0006C12.62 14.0006 13.6667 12.9552 13.6667 11.6686C13.6667 10.3806 12.62 9.3339 11.3334 9.3339Z" fill="#1A1A1A"/> </svg>';
const whatsappSvg = '<svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" fill="#1A1A1A"/></svg>';

interface ContactFormProps {
    title?: string;
    description?: string;
    form?: ContactFormConfig | null;
    pageUrls?: PageUrlMap;
    contractsPageUrl?: string;
    language?: string;
    defaultLanguage?: string;
    initialFormDetails?: FormDetails | null; // Server-side'da yüklenen form detayları
    translations?: Record<string, string>; // Server-side'da yüklenen translations (client-side fetch'i önler)
    /** Layout ile gelen iletişim bilgisi; verilirse client fetch yapılmaz */
    initialContactInfo?: ContactInfo | null;
}

export default function ContactForm({
    title,
    description,
    form = null,
    pageUrls,
    contractsPageUrl,
    language,
    defaultLanguage,
    initialFormDetails = null, // Server-side'da yüklenen form detayları
    translations: serverTranslations, // Server-side'da yüklenen translations
    initialContactInfo = null,
}: ContactFormProps) {
    const { getVariable } = useLanguageVariables();
    const countries = useCountries();
    
    // Title ve description için fallback değerler
    const displayTitle = title || getVariable('contact_form.title', 'Anymore question?');
    const displayDescription = description || getVariable('contact_form.description', 'Lorem ipsum dolor sit amet consectetur. Amet pellentesque et tristique in nibh quis interdum. In ut diam diam proin sit proin metus. Ut pulvinar potenti sit in mi ac nec eu. Sem laoreet amet amet id at nunc suspendisse amet.');
    const [contactInfo, setContactInfo] = useState<ContactInfo | null>(initialContactInfo ?? null);
    const [loading, setLoading] = useState(!initialContactInfo);
    const [submitting, setSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState<{ type: 'success' | 'error' | null; message: string }>({ type: null, message: '' });
    const [phoneValue, setPhoneValue] = useState('');
    const [formFieldValues, setFormFieldValues] = useState<Record<string, string>>({});
    // Server-side'da yüklenen form detaylarını initial state olarak kullan
    const [formDetails, setFormDetails] = useState<FormDetails | null>(initialFormDetails);
    const [formDetailsLoading, setFormDetailsLoading] = useState(false);
    const [formDetailsError, setFormDetailsError] = useState<string | null>(null);
    const [agreementsState, setAgreementsState] = useState<Record<number, boolean>>(() => {
        // Server-side'da yüklenen form detayları varsa, agreements state'ini başlat
        if (initialFormDetails?.agreements) {
            const state: Record<number, boolean> = {};
            (initialFormDetails.agreements.required || []).forEach((agreement) => {
                state[agreement.id] = true;
            });
            (initialFormDetails.agreements.optional || []).forEach((agreement) => {
                state[agreement.id] = false;
            });
            return state;
        }
        return {};
    });
    const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const formRef = useRef<HTMLFormElement | null>(null);
    const captchaWidgetIdRef = useRef<number | string | null>(null); // v2: number, Turnstile: string, v3: null
    const captchaContainerRef = useRef<HTMLDivElement | null>(null);
    const [captchaToken, setCaptchaToken] = useState<string>('');
    
    // Captcha config hook
    const { config: captchaConfig, isLoading: captchaConfigLoading, error: captchaConfigError } = useCaptchaConfig();
    
    // Debug: Captcha config durumunu logla
    useEffect(() => {
 
        
        // Error varsa detaylı log
        if (captchaConfigError) {
            console.error('[ContactForm] Captcha Config Error:', captchaConfigError);
        }
    }, [captchaConfigLoading, captchaConfigError, captchaConfig]);
    const [newsletterSubscribed, setNewsletterSubscribed] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedAgreementSlug, setSelectedAgreementSlug] = useState<string | null>(null);
    const [selectedAgreementId, setSelectedAgreementId] = useState<number | undefined>(undefined);
    const [selectedAgreementIsRequired, setSelectedAgreementIsRequired] = useState(false);

    const handleDynamicFieldChange = (name: string, value: string) => {
        setFormFieldValues((prev) => ({ ...prev, [name]: value }));
    };

    const titleSelectOptions = useMemo((): ContactFormSelectOption[] => {
        return CONTACT_TITLE_VALUES.map((value) => ({
            value,
            label: getVariable(
                `contact_form.title_${value.toLowerCase()}`,
                getVariable(`request_form.title_${value.toLowerCase()}`, value)
            ),
        }));
    }, [getVariable]);

    useEffect(() => {
        if (initialContactInfo) {
            setContactInfo(initialContactInfo);
            setLoading(false);
            return;
        }
        const fetchContactInfo = async () => {
            try {
                const lang = getLanguageFromPathname(window.location.pathname) || 'en';
                const response = await fetch(`/api/contact?language=${encodeURIComponent(lang)}`);
                
                if (!response.ok) {
                    console.error('Contact API error:', response.status);
                    return;
                }

                const payload = await response.json();
                if (payload.success && payload.data) {
                    const attrs = payload.data.attributes;
                    setContactInfo({
                        companyName: attrs.company_name,
                        phone: attrs.phone,
                        whatsapp: attrs.whatsapp,
                        email: attrs.email,
                        address: attrs.address,
                        lat: attrs.lat ? parseFloat(attrs.lat) : null,
                        lng: attrs.lng ? parseFloat(attrs.lng) : null,
                        workingHours: attrs.working_hours,
                        language: {
                            code: attrs.language?.code ?? null,
                            name: attrs.language?.name ?? null,
                        },
                    });
                }
            } catch (error) {
                console.error('Failed to fetch contact info:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchContactInfo();
    }, [initialContactInfo]);

    useEffect(() => {
        // Eğer server-side'da form detayları yüklenmişse, client-side fetch yapma
        if (initialFormDetails) {
            return;
        }
        
        if (!form?.id && !form?.slug) {
            setFormDetails(null);
            setFormDetailsLoading(false);
            setFormDetailsError(null);
            setAgreementsState({});
            return;
        }

        const controller = new AbortController();
        const params = new URLSearchParams();
        if (form?.id) {
            params.set('form_id', String(form.id));
        } else if (form?.slug) {
            params.set('form_slug', form.slug);
        }

        const fetchFormDetails = async () => {
            try {
                setFormDetailsError(null);
                const url = `/api/forms/details?${params.toString()}`;
                
                
                // Loading state'i gecikmeli göster (200ms içinde response gelirse gösterme)
                loadingTimeoutRef.current = setTimeout(() => {
                    if (!controller.signal.aborted) {
                        setFormDetailsLoading(true);
                    }
                }, 200);
                
                const response = await fetch(url, {
                    signal: controller.signal,
                });
                const payload = await response.json();
                
                // Eğer hızlı geldiyse loading timeout'unu iptal et
                if (loadingTimeoutRef.current) {
                    clearTimeout(loadingTimeoutRef.current);
                    loadingTimeoutRef.current = null;
                }
                if (!controller.signal.aborted) {
                    setFormDetailsLoading(false);
                }
                
                if (!response.ok || !payload?.success) {
                    const errorMessage = payload?.message || payload?.error || `HTTP ${response.status}: ${getVariable('contact_form.error.form_details_not_found', 'Form bilgileri alınamadı')}`;
                    console.error('[ContactForm] Form details error:', errorMessage, payload);
                    throw new Error(errorMessage);
                }
                if (!payload?.data) {
                    console.error('[ContactForm] Missing data in response:', payload);
                    throw new Error(getVariable('contact_form.error.form_data_missing', 'Form detayları response\'unda data bulunamadı'));
                }
                const details = payload.data as FormDetails;
                
                setFormDetails(details);
                const nextAgreementsState: Record<number, boolean> = {};
                // Zorunlu sözleşmeler varsayılan olarak onaylı (otomatik oluşturulacak)
                (details?.agreements?.required || []).forEach((agreement) => {
                    nextAgreementsState[agreement.id] = true;
                });
                // Opsiyonel sözleşmeler varsayılan olarak onaylı değil
                (details?.agreements?.optional || []).forEach((agreement) => {
                    nextAgreementsState[agreement.id] = false;
                });
                setAgreementsState(nextAgreementsState);
            } catch (error) {
                if (controller.signal.aborted) {
                    return;
                }
                console.error('Failed to fetch form details:', error);
                setFormDetails(null);
                setAgreementsState({});
                setFormDetailsError(error instanceof Error ? error.message : getVariable('contact_form.error.form_details_not_found', 'Form bilgileri alınamadı'));
            } finally {
                // Loading timeout'unu temizle
                if (loadingTimeoutRef.current) {
                    clearTimeout(loadingTimeoutRef.current);
                    loadingTimeoutRef.current = null;
                }
                if (!controller.signal.aborted) {
                    setFormDetailsLoading(false);
                }
            }
        };

        fetchFormDetails();

        return () => {
            controller.abort();
            if (loadingTimeoutRef.current) {
                clearTimeout(loadingTimeoutRef.current);
                loadingTimeoutRef.current = null;
            }
        };
    }, [form?.id, form?.slug, getVariable, initialFormDetails]);

    // Captcha script'ini yükle ve widget'ı render et (provider'a göre)
    useEffect(() => {
    

        // Config yükleniyorsa veya captcha aktif değilse bekle
        if (captchaConfigLoading) {
            
            return;
        }

        if (!captchaConfig?.is_enabled) {
            
            return;
        }

        if (!captchaConfig?.site_key || !captchaConfig?.provider) {
      
            return;
        }

        const provider = captchaConfig.provider;
        const siteKey = captchaConfig.site_key;
        const scriptUrl = captchaConfig.script_url;
        
        

        const initCaptcha = async () => {
            try {
                if (provider === 'google_recaptcha_v2') {
                    // reCAPTCHA v2
                    await loadRecaptchaScript(scriptUrl || undefined);
                    
                    const checkContainer = setInterval(() => {
                        const container = document.getElementById('contact-captcha-widget-container');
                        if (container && siteKey) {
                            clearInterval(checkContainer);
                            renderRecaptchaWidget(
                                'contact-captcha-widget-container',
                                siteKey,
                                (token: string) => {
                                    setCaptchaToken(token);
                                }
                            ).then((widgetId) => {
                                captchaWidgetIdRef.current = widgetId;
                                
                            }).catch((error) => {
                                console.error('[ContactForm] reCAPTCHA v2 widget render edilemedi:', error);
                            });
                        }
                    }, 100);

                    setTimeout(() => {
                        clearInterval(checkContainer);
                    }, 10000);
                } else if (provider === 'google_recaptcha_v3' || provider === 'google_recaptcha_enterprise') {
                    // reCAPTCHA v3 veya Enterprise - Widget yok; Enterprise için enterprise.js kullan
                    await loadRecaptchaV3Script(siteKey, provider === 'google_recaptcha_enterprise' ? scriptUrl : undefined);
                    // v3/Enterprise için token form submit sırasında alınacak
                } else if (provider === 'cloudflare_turnstile') {
                    // Cloudflare Turnstile
                    await loadTurnstileScript(scriptUrl || undefined);
                    
                    const checkContainer = setInterval(() => {
                        const container = document.getElementById('contact-captcha-widget-container');
                        if (container && siteKey) {
                            clearInterval(checkContainer);
                            renderTurnstileWidget(
                                'contact-captcha-widget-container',
                                siteKey,
                                (token: string) => {
                                    setCaptchaToken(token);
                                }
                            ).then((widgetId) => {
                                captchaWidgetIdRef.current = widgetId;
                                
                            }).catch((error) => {
                                console.error('[ContactForm] Turnstile widget render edilemedi:', error);
                            });
                        }
                    }, 100);

                    setTimeout(() => {
                        clearInterval(checkContainer);
                    }, 10000);
                }
            } catch (error) {
                console.error('[ContactForm] Captcha yüklenemedi:', error);
            }
        };

        // Form render edildikten sonra başlat
        const timeout = setTimeout(() => {
            initCaptcha();
        }, 1000);

        return () => {
            clearTimeout(timeout);
            // Cleanup: Widget'ı reset et
            if (captchaWidgetIdRef.current !== null && captchaConfig?.provider) {
                if (captchaConfig.provider === 'google_recaptcha_v2') {
                    resetRecaptchaWidget(captchaWidgetIdRef.current as number);
                } else if (captchaConfig.provider === 'cloudflare_turnstile') {
                    resetTurnstileWidget(captchaWidgetIdRef.current as string);
                    removeTurnstileWidget(captchaWidgetIdRef.current as string);
                }
            }
        };
    }, [captchaConfig, captchaConfigLoading]);

    // Build information array from API data
    const information = useMemo(() => {
        if (!contactInfo) {
            return [];
        }

        const items = [];
        
        if (contactInfo.phone) {
            items.push({
                title: contactInfo.phone,
                link: `tel:${contactInfo.phone.replace(/\s/g, '')}`,
                svg: phoneSvg,
            });
        }
        if (contactInfo.whatsapp) {
            items.push({
                title: contactInfo.whatsapp,
                link: `https://wa.me/${contactInfo.whatsapp.replace(/\D/g, '')}`,
                svg: whatsappSvg,
            });
        }
        if (contactInfo.email) {
            items.push({
                title: contactInfo.email,
                link: `mailto:${contactInfo.email}`,
                svg: emailSvg,
            });
        }
        if (contactInfo.address) {
            items.push({
                title: contactInfo.address,
                link: '#',
                svg: addressSvg,
            });
        }

        return items;
    }, [contactInfo]);

    const requiredAgreements = formDetails?.agreements?.required ?? [];
    const optionalAgreements = formDetails?.agreements?.optional ?? [];

    // Sözleşme onay metnini çözer: agreement.text.{slug} -> agreement.{required|optional}.default
    const resolveAgreementTemplate = (agreement: AgreementItem): string => {
        const specific = getVariable(`agreement.text.${agreement.slug}`, '');
        if (specific) return specific;
        return agreement.is_required
            ? getVariable('agreement.required.default', '{agreement}')
            : getVariable('agreement.optional.default', '{agreement}');
    };

    // Tıklanabilir başlık linki (modal açar) — cümle içinde doğal akması için inline <span>
    const openAgreementModalFor = (agreement: AgreementItem) => {
        setSelectedAgreementSlug(agreement.slug);
        setSelectedAgreementId(agreement.id);
        setSelectedAgreementIsRequired(agreement.is_required);
        setModalOpen(true);
    };
    const renderAgreementLink = (agreement: AgreementItem) => (
        <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                openAgreementModalFor(agreement);
            }}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.stopPropagation();
                    openAgreementModalFor(agreement);
                }
            }}
            className="text-alsero-first hover:text-alsero-first font-bold underline transition-all ease-in-out cursor-pointer"
        >
            {agreement.title}
        </span>
    );

    // Şablonu link ile birlikte render eder: {agreement} -> link
    const renderAgreementLabel = (agreement: AgreementItem) => {
        const { before, after } = splitAgreementTemplate(resolveAgreementTemplate(agreement));
        return (
            <>
                {before}
                {renderAgreementLink(agreement)}
                {after}
            </>
        );
    };

    // Sözleşme URL'lerini oluştur
    const getAgreementUrl = useMemo(() => {
        return (agreementSlug: string): string => {
            if (!language || !defaultLanguage) {
                if (process.env.NODE_ENV === 'development') {
                    console.error('ContactForm: language and defaultLanguage props are required');
                }
                return '#';
            }
            const currentLang = language;
            const defaultLang = defaultLanguage;
            
            const baseUrl = resolveUrlFromMap(
                pageUrls,
                'contracts',
                currentLang,
                defaultLang,
                contractsPageUrl || '/contracts'
            );

            return buildLocalizedLink(baseUrl, agreementSlug, defaultLang);
        };
    }, [pageUrls, contractsPageUrl, language, defaultLanguage]);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setSubmitting(true);
        setSubmitStatus({ type: null, message: '' });

        const formData = new FormData(e.currentTarget);
        
        // Dinamik form alanlarını topla ve required alanları kontrol et
        const formPayload: Record<string, string> = {};
        const missingRequiredFields: string[] = [];
        
        if (formDetails?.fields && Array.isArray(formDetails.fields)) {
            formDetails.fields.forEach((field: any) => {
                const fieldName = field.name || field.field || '';
                if (fieldName) {
                    const isRequired = field.required !== false && (field.required === true || field.required === 'true' || field.required === 1);
                    let fieldValue = '';
                    
                    // Phone field için özel işlem
                    if (field.type === 'tel' || field.type === 'phone' || fieldName.toLowerCase().includes('phone')) {
                        fieldValue = phoneValue || (formData.get(fieldName) as string) || '';
                    } else {
                        fieldValue = (formFieldValues[fieldName] ?? (formData.get(fieldName) as string) ?? '').trim();
                    }
                    
                    // Boş string'leri temizle (trim)
                    fieldValue = fieldValue.trim();
                    
                    // Required alan kontrolü
                    if (isRequired && !fieldValue) {
                        const fieldLabel = field.label || field.title || fieldName;
                        missingRequiredFields.push(fieldLabel);
                    }
                    
                    // Sadece dolu alanları gönder (boş string'leri gönderme)
                    if (fieldValue) {
                        formPayload[fieldName] = fieldValue;
                    }
                }
            });
        } else {
            // Fallback: Eğer form fields yoksa, varsayılan alanları kullan
            const name = ((formData.get('name') as string) || '').trim();
            const email = ((formData.get('email') as string) || '').trim();
            const phone = (phoneValue || (formData.get('phone') as string) || '').trim();
            const message = ((formData.get('message') as string) || '').trim();
            
            // Required alan kontrolü
            if (!name) missingRequiredFields.push(getVariable('contact_form.field.name.label', 'Name'));
            if (!email) missingRequiredFields.push(getVariable('contact_form.field.email.label', 'Email'));
            if (!phone) missingRequiredFields.push(getVariable('contact_form.field.phone.label', 'Phone'));
            if (!message) missingRequiredFields.push(getVariable('contact_form.field.message.label', 'Message'));
            
            // Sadece dolu alanları gönder
            if (name) formPayload.name = name;
            if (email) formPayload.email = email;
            if (phone) formPayload.phone = phone;
            if (message) formPayload.message = message;
        }
        
        // Required alanlar eksikse hata göster
        if (missingRequiredFields.length > 0) {
            const errorMessage = getVariable(
                'contact_form.error.required_fields_missing',
                'Tüm zorunlu alanlar doldurulmalıdır'
            ) + ': ' + missingRequiredFields.join(', ');
            setSubmitStatus({
                type: 'error',
                message: errorMessage,
            });
            setSubmitting(false);
            return;
        }

        const agreementPayload = formDetails
            ? [...requiredAgreements, ...optionalAgreements].map((agreement) => ({
                id: agreement.id,
                title: agreement.title,
                slug: agreement.slug,
                required: agreement.is_required,
                accepted: Boolean(agreementsState[agreement.id]),
            }))
            : [];

        const targetFormPayload = form?.id || form?.slug
            ? {
                ...(form?.id ? { form_id: form.id } : {}),
                ...(form?.slug ? { form_slug: form.slug } : {}),
            }
            : { form_slug: 'contactform' };

        // Captcha token kontrolü (provider'a göre)
        let captchaToken = '';
        if (captchaConfig?.is_enabled && captchaConfig?.site_key && captchaConfig?.provider) {
            if (captchaConfig.provider === 'google_recaptcha_v2') {
                captchaToken = getRecaptchaToken(captchaWidgetIdRef.current as number | undefined);
                if (!captchaToken) {
                    setSubmitStatus({
                        type: 'error',
                        message: getVariable('common.captcha_required', 'Lütfen robot doğrulamayı tamamlayın.'),
                    });
                    setSubmitting(false);
                    return;
                }
            } else if (captchaConfig.provider === 'google_recaptcha_v3' || captchaConfig.provider === 'google_recaptcha_enterprise') {
                try {
                    await loadRecaptchaV3Script(
                        captchaConfig.site_key,
                        captchaConfig.provider === 'google_recaptcha_enterprise' ? captchaConfig.script_url ?? undefined : undefined
                    );
                    captchaToken = await getRecaptchaV3Token(captchaConfig.site_key, 'contact_form');
                } catch (error) {
                    console.error('reCAPTCHA v3 token alınamadı:', error);
                    setSubmitStatus({
                        type: 'error',
                        message: getVariable('common.captcha_required', 'Lütfen robot doğrulamayı tamamlayın.'),
                    });
                    setSubmitting(false);
                    return;
                }
                if (!captchaToken) {
                    setSubmitStatus({
                        type: 'error',
                        message: getVariable('common.captcha_required', 'Lütfen robot doğrulamayı tamamlayın.'),
                    });
                    setSubmitting(false);
                    return;
                }
            } else if (captchaConfig.provider === 'cloudflare_turnstile') {
                captchaToken = getTurnstileToken(captchaWidgetIdRef.current as string | undefined);
                if (!captchaToken) {
                    setSubmitStatus({
                        type: 'error',
                        message: getVariable('common.captcha_required', 'Lütfen robot doğrulamayı tamamlayın.'),
                    });
                    setSubmitting(false);
                    return;
                }
            }
        }

        const submitPayload = {
            ...targetFormPayload,
            ...formPayload,
            language_id: formDetails?.language?.id ?? form?.languageId ?? undefined,
            newsletter_subscribed: newsletterSubscribed,
            agreements: agreementPayload.length ? agreementPayload : undefined,
            recaptcha_token: captchaToken || undefined, // Sadece varsa gönder
        };
        
        

        try {
            const pageLanguage =
                language ||
                getLanguageFromPathname(typeof window !== 'undefined' ? window.location.pathname : '') ||
                'en';
            const response = await fetch('/api/form/submit', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    'Accept-Language': pageLanguage,
                },
                body: JSON.stringify(submitPayload),
            });

            // Response'un boş olup olmadığını kontrol et
            const contentType = response.headers.get('content-type');
            let result: any = {};
            
            if (contentType && contentType.includes('application/json')) {
                try {
                    const text = await response.text();
                    if (text) {
                        result = JSON.parse(text);
                    }
                } catch (parseError) {
                    console.error('[ContactForm] JSON parse error:', parseError);
                    result = {
                        success: false,
                        message: getVariable('contact_form.error.invalid_response', 'Sunucudan geçersiz yanıt alındı'),
                    };
                }
            } else {
                // JSON değilse, response text'ini al
                const text = await response.text();
                result = {
                    success: false,
                    message: text || getVariable('contact_form.error.invalid_response', 'Sunucudan geçersiz yanıt alındı'),
                };
            }
    

            // HTTP status kontrolü veya result.success kontrolü
            if (!response.ok || result.success === false) {
                // Backend'den gelen hata mesajını kullan
                // errorResponse formatı: { error: string, status: number }
                // ValidationException formatı: { success: false, message: string, errors: object }
                let errorMessage = result.message || 
                                   result.error || 
                                   (result.errors && typeof result.errors === 'object' 
                                       ? Object.values(result.errors).flat().join(', ') 
                                       : null);
                
                // Eğer hala mesaj yoksa, status code'a göre genel mesaj
                if (!errorMessage) {
                    if (response.status === 422) {
                        errorMessage = getVariable('contact_form.error.validation_failed', 'Form validasyon hatası. Lütfen tüm alanları kontrol edin.');
                    } else if (response.status === 404) {
                        errorMessage = getVariable('contact_form.error.form_not_found', 'Form bulunamadı.');
                    } else if (response.status === 403) {
                        errorMessage = getVariable('contact_form.error.access_denied', 'Erişim engellendi.');
                    } else if (response.status >= 500) {
                        errorMessage = getVariable('contact_form.error.server_error', 'Sunucu hatası. Lütfen daha sonra tekrar deneyin.');
                    } else {
                        errorMessage = getVariable('contact_form.error.submit_failed', 'Bir hata oluştu. Lütfen tekrar deneyin.');
                    }
                }
                
                console.error('[ContactForm] Submit failed:', {
                    status: response.status,
                    ok: response.ok,
                    result,
                    errorMessage,
                });
                
                setSubmitStatus({
                    type: 'error',
                    message: errorMessage,
                });
                setSubmitting(false);
                return;
            }

            // Success kontrolü (result.success === true olmalı)
            if (result.success === true) {
                setSubmitStatus({
                    type: 'success',
                    message: getVariable('contact_form.success.submitted', 'Your message has been sent successfully!'),
                });
                // Form'u temizle
                if (formRef.current) {
                    formRef.current.reset();
                } else if (e.currentTarget) {
                    e.currentTarget.reset();
                }
                setPhoneValue(''); // PhoneInput'u da temizle
                setCaptchaToken(''); // Token'ı temizle
                
                // reCAPTCHA widget'ını reset et
                // Captcha widget'ını reset et
                if (captchaWidgetIdRef.current !== null) {
                    if (captchaConfig?.provider === 'google_recaptcha_v2') {
                        resetRecaptchaWidget(captchaWidgetIdRef.current as number);
                    } else if (captchaConfig?.provider === 'cloudflare_turnstile') {
                        resetTurnstileWidget(captchaWidgetIdRef.current as string);
                    }
                }
                
                // Zorunlu sözleşmeler varsayılan olarak onaylı kalmalı
                setAgreementsState((prev) => {
                    const resetState: Record<number, boolean> = {};
                    // Zorunlu sözleşmeler varsayılan olarak onaylı
                    (formDetails?.agreements?.required || []).forEach((agreement) => {
                        resetState[agreement.id] = true;
                    });
                    // Opsiyonel sözleşmeler varsayılan olarak onaylı değil
                    (formDetails?.agreements?.optional || []).forEach((agreement) => {
                        resetState[agreement.id] = false;
                    });
                    return resetState;
                });
            } else {
                const errorMessage = result.message || 
                                   result.error || 
                                   (result.errors && typeof result.errors === 'object' 
                                       ? Object.values(result.errors).flat().join(', ') 
                                       : null) ||
                                   getVariable('contact_form.error.submit_failed', 'Bir hata oluştu. Lütfen tekrar deneyin.');
                
                setSubmitStatus({
                    type: 'error',
                    message: errorMessage,
                });
            }
        } catch (error) {
            console.error('[ContactForm] Form submission error:', error);
            const errorMessage = error instanceof Error 
                ? error.message 
                : getVariable('contact_form.error.submit_failed', 'Bir hata oluştu. Lütfen tekrar deneyin.');
            setSubmitStatus({
                type: 'error',
                message: errorMessage,
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <motion.section
            className="relative"
            initial="hidden"
            animate="visible"
            variants={pageVariants}
        >
            <div className="absolute inset-0 bg-[url(/static/map-contact.png)] bg-position-[-100px_50px] bg-no-repeat bg-size-[945px_777px] pointer-events-none -z-10"></div>
            <div className="container mx-auto py-16 sm:py-20 md:py-24 lg:py-32 xl:py-[70px] relative z-10">
                <div className="grid grid-cols-12">
                    <motion.div
                        className="col-span-12 md:col-span-6 lg:col-span-5"
                        variants={staggerContainerVariants}
                    >
                        <div className="flex flex-col">
                            <motion.h2
                                className="mb-4 sm:mb-5 md:mb-6 lg:mb-7 font-bold text-alsero-dark-gray text-2xl sm:text-3xl md:text-4xl"
                                variants={headerVariants}
                                data-prop-path="title"
                            >
                                {displayTitle}
                            </motion.h2>
                            <motion.p
                                className="mb-8 sm:mb-10 md:mb-12 lg:mb-14 text-base sm:text-lg text-alsero-dark-gray"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.2 }}
                                data-prop-path="description"
                            >
                                {displayDescription}
                            </motion.p>
                            <motion.ul
                                className="flex flex-col gap-4 sm:gap-5 md:gap-6"
                                variants={staggerContainerVariants}
                            >
                                {information.map((subItem, subIndex) => (
                                    <motion.li
                                        key={subIndex}
                                        variants={listItemVariants}
                                        whileHover={{ x: 10, scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                    >
                                        <Link href={subItem.link} className="text-lg sm:text-xl text-alsero-dark-gray flex flex-row gap-2 items-center" {...(subItem.link.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
                                            {subItem.svg && <div dangerouslySetInnerHTML={{ __html: sanitizeCmsHtml(subItem.svg) }}></div>}
                                            {subItem.title}
                                        </Link>
                                    </motion.li>
                                ))}
                            </motion.ul>
                            {contactInfo?.workingHours ? (
                                <motion.div
                                    className="mt-6 pt-6 border-t border-alsero-dark-gray/20"
                                    variants={listItemVariants}
                                >
                                    <p className="text-sm font-medium text-alsero-dark-gray/80 mb-1">
                                        {getVariable('contact_form.working_hours', 'Çalışma saatleri')}
                                    </p>
                                    <p className="text-lg sm:text-xl text-alsero-dark-gray whitespace-pre-line">
                                        {contactInfo.workingHours}
                                    </p>
                                </motion.div>
                            ) : null}
                        </div>
                    </motion.div>
                    <motion.div
                        className="col-span-12 md:col-span-6 lg:col-span-5 lg:col-start-7"
                        variants={slideFromRightVariants}
                    >
                        <motion.form
                            ref={formRef}
                            variants={formItemVariants}
                            initial="hidden"
                            animate="visible"
                            transition={{ staggerChildren: 0.1 }}
                            onSubmit={handleSubmit}
                        >
                            <div className="grid grid-cols-12 gap-4 sm:gap-6 md:gap-8">
                                {formDetailsLoading && (
                                    <motion.div className="col-span-12" variants={formItemVariants} {...motionItemProps}>
                                        <div className="rounded-xl border border-dashed border-alsero-dark-gray/20 bg-white/80 px-4 py-3 shadow-sm backdrop-blur">
                                            <p className="text-sm text-gray-500">{getVariable('contact_form.loading.form_loading', 'Form yükleniyor...')}</p>
                                        </div>
                                    </motion.div>
                                )}
                                {formDetailsError && (
                                    <motion.div className="col-span-12" variants={formItemVariants} {...motionItemProps}>
                                        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 shadow-sm">
                                            <p className="text-sm text-red-600">{formDetailsError}</p>
                                        </div>
                                    </motion.div>
                                )}
                                {/* Dinamik form alanları */}
                                {formDetails?.fields && Array.isArray(formDetails.fields) && formDetails.fields.length > 0 ? (
                                    formDetails.fields.map((field: any, index: number) => {
                                        const fieldName = field.name || field.field || `field_${index}`;
                                        const fieldType = field.type || 'text';
                                        const fieldLabel = field.label || field.title || fieldName;
                                        const fieldPlaceholder = field.placeholder || getVariable(
                                            `contact_form.field.${fieldName.toLowerCase()}.placeholder`,
                                            getVariable('contact_form.field.generic.placeholder', `Enter ${fieldLabel.toLowerCase()}`)
                                        );
                                        const isRequired = field.required !== false && (field.required === true || field.required === 'true' || field.required === 1);
                                        let optionList: ContactFormSelectOption[] = Array.isArray(field.options)
                                            ? field.options.map((o: string | { value: string; label: string }) =>
                                                  typeof o === 'string' ? { value: o, label: o } : { value: o.value, label: o.label || o.value }
                                              )
                                            : [];
                                        const formBuilderValues = (field as { values?: Array<{ value?: string; label?: string }> }).values;
                                        if (optionList.length === 0 && Array.isArray(formBuilderValues)) {
                                            optionList = formBuilderValues
                                                .map((o) => ({
                                                    value: String(o.value ?? o.label ?? ''),
                                                    label: String(o.label ?? o.value ?? ''),
                                                }))
                                                .filter((o) => o.value !== '');
                                        }

                                        if (
                                            fieldType === 'country' ||
                                            fieldName === 'country_id' ||
                                            (fieldName.toLowerCase().includes('country') && fieldName !== 'country')
                                        ) {
                                            const countryPlaceholder =
                                                fieldPlaceholder ||
                                                getVariable('request_form.country_placeholder', getVariable('contact_form.country_placeholder', 'Select country'));
                                            const countryOptions: ContactFormSelectOption[] = [
                                                { value: '', label: countryPlaceholder },
                                                ...countries.map((c) => ({ value: String(c.id), label: c.name })),
                                            ];
                                            const currentCountry = formFieldValues[fieldName] ?? '';
                                            const selectedCountry = currentCountry
                                                ? countryOptions.find((o) => o.value === currentCountry) ?? null
                                                : null;
                                            return (
                                                <motion.div
                                                    key={fieldName}
                                                    className="col-span-12"
                                                    variants={formItemVariants}
                                                    {...motionItemProps}
                                                >
                                                    <div className="flex flex-col gap-5">
                                                        <label htmlFor={`contact-form-${fieldName}`} className="font-semibold text-base sm:text-lg text-alsero-dark-gray">
                                                            {fieldLabel}
                                                            {isRequired && <span className="text-red-500 ml-1">*</span>}
                                                        </label>
                                                        <ReactSelect
                                                            inputId={`contact-form-${fieldName}`}
                                                            value={selectedCountry}
                                                            onChange={(opt) =>
                                                                handleDynamicFieldChange(fieldName, (opt as { value: string } | null)?.value ?? '')
                                                            }
                                                            options={countryOptions}
                                                            placeholder={countryPlaceholder}
                                                            isClearable={!isRequired}
                                                            isSearchable
                                                            styles={searchableSelectStyles}
                                                            noOptionsMessage={() =>
                                                                getVariable('contact_form.no_options', getVariable('request_form.no_options', 'No options'))
                                                            }
                                                            classNamePrefix="contact-form-select"
                                                            components={{ Input: NoAutocompleteInput }}
                                                        />
                                                    </div>
                                                </motion.div>
                                            );
                                        }

                                        if (fieldType === 'select' || fieldName.toLowerCase() === 'title') {
                                            const fromField: ContactFormSelectOption[] = optionList.length ? optionList : titleSelectOptions;
                                            const selectPlaceholder =
                                                fieldPlaceholder ||
                                                getVariable('contact_form.select_placeholder', getVariable('request_form.title_placeholder', 'Select'));
                                            const options: ContactFormSelectOption[] = !isRequired
                                                ? [{ value: '', label: selectPlaceholder }, ...fromField]
                                                : fromField;
                                            const currentValue = formFieldValues[fieldName] ?? '';
                                            const selectedOption = currentValue
                                                ? options.find((o: ContactFormSelectOption) => o.value === currentValue) ?? null
                                                : null;
                                            return (
                                                <motion.div
                                                    key={fieldName}
                                                    className="col-span-12"
                                                    variants={formItemVariants}
                                                    {...motionItemProps}
                                                >
                                                    <div className="flex flex-col gap-5">
                                                        <label htmlFor={`contact-form-${fieldName}`} className="font-semibold text-base sm:text-lg text-alsero-dark-gray">
                                                            {fieldLabel}
                                                            {isRequired && <span className="text-red-500 ml-1">*</span>}
                                                        </label>
                                                        <ReactSelect
                                                            inputId={`contact-form-${fieldName}`}
                                                            value={selectedOption}
                                                            onChange={(opt) =>
                                                                handleDynamicFieldChange(fieldName, (opt as { value: string } | null)?.value ?? '')
                                                            }
                                                            options={options}
                                                            placeholder={selectPlaceholder}
                                                            isClearable={!isRequired}
                                                            isSearchable
                                                            styles={searchableSelectStyles}
                                                            noOptionsMessage={() =>
                                                                getVariable('contact_form.no_options', getVariable('request_form.no_options', 'No options'))
                                                            }
                                                            classNamePrefix="contact-form-select"
                                                            components={{ Input: NoAutocompleteInput }}
                                                        />
                                                    </div>
                                                </motion.div>
                                            );
                                        }

                                        // Phone field için özel render
                                        if (fieldType === 'tel' || fieldType === 'phone' || fieldName.toLowerCase().includes('phone')) {
                                            return (
                                                <motion.div
                                                    key={fieldName}
                                                    className="col-span-12"
                                                    variants={formItemVariants}
                                                    {...motionItemProps}
                                                >
                                                    <div className="flex flex-col gap-5">
                                                        <label htmlFor={fieldName} className="font-semibold text-base sm:text-lg text-alsero-dark-gray">
                                                            {fieldLabel}
                                                            {isRequired && <span className="text-red-500 ml-1">*</span>}
                                                        </label>
                                                        <motion.div
                                                            whileHover={{ scale: 1.005, borderColor: "rgba(0, 0, 0, 0.15)" }}
                                                            whileFocus={{ scale: 1.01, borderColor: "rgba(227, 30, 36, 0.3)" }}
                                                            transition={{ type: "spring" as const, stiffness: 300, damping: 20 }}
                                                        >
                                                            <PhoneInput
                                                                name={fieldName}
                                                                defaultCountry="tr"
                                                                required={isRequired}
                                                                value={phoneValue}
                                                                onChange={(value) => setPhoneValue(value)}
                                                                placeholder={fieldPlaceholder + (isRequired ? '*' : '')}
                                                                className="border border-[rgba(0,0,0,0.08)] focus-within:border-[rgba(0,0,0,0.40)] transition-all ease-in-out duration-300 rounded-lg !text-[#4F545A] font-medium text-sm sm:text-[15px] px-2 py-2"
                                                                inputClassName="!border-0 w-full"
                                                                countrySelectorStyleProps={{
                                                                    buttonClassName: "!border-0 !px-2 !rounded-lg transition-all ease-in-out duration-300 hover:bg-gray-50",
                                                                    dropdownStyleProps: {
                                                                        className: "!bg-white !border !border-[rgba(0,0,0,0.08)] !rounded-lg !shadow-tour-card !mt-1 !max-h-64 !overflow-y-auto",
                                                                        listItemClassName: "!px-3 !py-2 !hover:bg-alsero-soft-white !transition-colors !duration-200 !cursor-pointer !flex !items-center !gap-2",
                                                                        listItemFlagClassName: "!text-lg !mr-2",
                                                                        listItemCountryNameClassName: "!text-gray-700 !font-medium !text-sm",
                                                                        listItemDialCodeClassName: "!text-gray-500 !text-sm !ml-auto"
                                                                    }
                                                                }}
                                                                aria-label={fieldPlaceholder}
                                                                aria-required={isRequired}
                                                            />
                                                        </motion.div>
                                                    </div>
                                                </motion.div>
                                            );
                                        }
                                        
                                        // Textarea field için özel render
                                        if (fieldType === 'textarea' || fieldType === 'textbox') {
                                            return (
                                                <motion.div
                                                    key={fieldName}
                                                    className="col-span-12"
                                                    variants={formItemVariants}
                                                    {...motionItemProps}
                                                >
                                                    <div className="flex flex-col gap-5">
                                                        <label htmlFor={fieldName} className="font-semibold text-base sm:text-lg text-alsero-dark-gray">
                                                            {fieldLabel}
                                                            {isRequired && <span className="text-red-500 ml-1">*</span>}
                                                        </label>
                                                        <motion.textarea
                                                            id={fieldName}
                                                            name={fieldName}
                                                            required={isRequired}
                                                            aria-required={isRequired}
                                                            aria-label={fieldPlaceholder}
                                                            rows={field.rows || 5}
                                                            className="border border-[rgba(0,0,0,0.08)] focus:!border-[rgba(0,0,0,0.40)] transition-all ease-in-out duration-300 rounded-lg text-[#4F545A] font-medium text-sm sm:text-[15px] px-4 sm:px-6 py-3 sm:py-4 resize-y min-h-[120px]"
                                                            placeholder={fieldPlaceholder + (isRequired ? '*' : '')}
                                                            whileHover={{ scale: 1.005, borderColor: "rgba(0, 0, 0, 0.15)" }}
                                                            whileFocus={{ scale: 1.01, borderColor: "rgba(227, 30, 36, 0.3)" }}
                                                            transition={{ type: "spring" as const, stiffness: 300, damping: 20 }}
                                                        />
                                                    </div>
                                                </motion.div>
                                            );
                                        }
                                        
                                        // Email field için özel render
                                        if (fieldType === 'email' || fieldName.toLowerCase().includes('email')) {
                                            return (
                                                <motion.div
                                                    key={fieldName}
                                                    className="col-span-12"
                                                    variants={formItemVariants}
                                                    {...motionItemProps}
                                                >
                                                    <div className="flex flex-col gap-5">
                                                        <label htmlFor={fieldName} className="font-semibold text-base sm:text-lg text-alsero-dark-gray">
                                                            {fieldLabel}
                                                            {isRequired && <span className="text-red-500 ml-1">*</span>}
                                                        </label>
                                                        <motion.input
                                                            id={fieldName}
                                                            name={fieldName}
                                                            type="email"
                                                            required={isRequired}
                                                            aria-required={isRequired}
                                                            aria-label={fieldPlaceholder}
                                                            className="border border-[rgba(0,0,0,0.08)] focus:!border-[rgba(0,0,0,0.40)] transition-all ease-in-out duration-300 rounded-lg text-[#4F545A] font-medium text-sm sm:text-[15px] px-4 sm:px-6 py-3 sm:py-4"
                                                            placeholder={fieldPlaceholder + (isRequired ? '*' : '')}
                                                            whileHover={{ scale: 1.005, borderColor: "rgba(0, 0, 0, 0.15)" }}
                                                            whileFocus={{ scale: 1.01, borderColor: "rgba(227, 30, 36, 0.3)" }}
                                                            transition={{ type: "spring" as const, stiffness: 300, damping: 20 }}
                                                        />
                                                    </div>
                                                </motion.div>
                                            );
                                        }
                                        
                                        // Diğer text input alanları için genel render
                                        return (
                                            <motion.div
                                                key={fieldName}
                                                className="col-span-12"
                                                variants={formItemVariants}
                                                {...motionItemProps}
                                            >
                                                <div className="flex flex-col gap-5">
                                                    <label htmlFor={fieldName} className="font-semibold text-base sm:text-lg text-alsero-dark-gray">
                                                        {fieldLabel}
                                                        {isRequired && <span className="text-red-500 ml-1">*</span>}
                                                    </label>
                                                    <motion.input
                                                        id={fieldName}
                                                        name={fieldName}
                                                        type={fieldType}
                                                        required={isRequired}
                                                        aria-required={isRequired}
                                                        aria-label={fieldPlaceholder}
                                                        className="border border-[rgba(0,0,0,0.08)] focus:!border-[rgba(0,0,0,0.40)] transition-all ease-in-out duration-300 rounded-lg text-[#4F545A] font-medium text-sm sm:text-[15px] px-4 sm:px-6 py-3 sm:py-4"
                                                        placeholder={fieldPlaceholder + (isRequired ? '*' : '')}
                                                        whileHover={{ scale: 1.005, borderColor: "rgba(0, 0, 0, 0.15)" }}
                                                        whileFocus={{ scale: 1.01, borderColor: "rgba(227, 30, 36, 0.3)" }}
                                                        transition={{ type: "spring" as const, stiffness: 300, damping: 20 }}
                                                    />
                                                </div>
                                            </motion.div>
                                        );
                                    })
                                ) : (
                                    // Fallback: Eğer form fields yoksa veya form yüklenmediyse, varsayılan alanları göster
                                    !formDetailsLoading && (
                                        <>
                                            <motion.div className="col-span-12" variants={formItemVariants} {...motionItemProps}>
                                                <div className="flex flex-col gap-5">
                                                    <label htmlFor="contact-name" className="font-semibold text-base sm:text-lg text-alsero-dark-gray">
                                                        {getVariable('contact_form.field.name.label', 'Name')}
                                                    </label>
                                                    <motion.input
                                                        id="contact-name"
                                                        name="name"
                                                        type="text"
                                                        required
                                                        aria-required="true"
                                                        aria-label={getVariable('contact_form.field.name.aria_label', 'Enter your name')}
                                                        className="border border-[rgba(0,0,0,0.08)] focus:!border-[rgba(0,0,0,0.40)] transition-all ease-in-out duration-300 rounded-lg text-[#4F545A] font-medium text-sm sm:text-[15px] px-4 sm:px-6 py-3 sm:py-4"
                                                        placeholder={getVariable('contact_form.field.name.placeholder', 'Enter your name*')}
                                                        whileHover={{ scale: 1.005, borderColor: "rgba(0, 0, 0, 0.15)" }}
                                                        whileFocus={{ scale: 1.01, borderColor: "rgba(227, 30, 36, 0.3)" }}
                                                        transition={{ type: "spring" as const, stiffness: 300, damping: 20 }}
                                                    />
                                                </div>
                                            </motion.div>
                                            <motion.div className="col-span-12" variants={formItemVariants} {...motionItemProps}>
                                                <div className="flex flex-col gap-5">
                                                    <label htmlFor="contact-email" className="font-semibold text-base sm:text-lg text-alsero-dark-gray">
                                                        {getVariable('contact_form.field.email.label', 'Email')}
                                                    </label>
                                                    <motion.input
                                                        id="contact-email"
                                                        name="email"
                                                        type="email"
                                                        required
                                                        aria-required="true"
                                                        aria-label={getVariable('contact_form.field.email.aria_label', 'Enter your email address')}
                                                        className="border border-[rgba(0,0,0,0.08)] focus:!border-[rgba(0,0,0,0.40)] transition-all ease-in-out duration-300 rounded-lg text-[#4F545A] font-medium text-sm sm:text-[15px] px-4 sm:px-6 py-3 sm:py-4"
                                                        placeholder={getVariable('contact_form.field.email.placeholder', 'Enter your email*')}
                                                        whileHover={{ scale: 1.005, borderColor: "rgba(0, 0, 0, 0.15)" }}
                                                        whileFocus={{ scale: 1.01, borderColor: "rgba(227, 30, 36, 0.3)" }}
                                                        transition={{ type: "spring" as const, stiffness: 300, damping: 20 }}
                                                    />
                                                </div>
                                            </motion.div>
                                            <motion.div className="col-span-12" variants={formItemVariants} {...motionItemProps}>
                                                <div className="flex flex-col gap-5">
                                                    <label htmlFor="contact-phone" className="font-semibold text-base sm:text-lg text-alsero-dark-gray">
                                                        {getVariable('contact_form.field.phone.label', 'Phone')}
                                                    </label>
                                                    <motion.div
                                                        whileHover={{ scale: 1.005, borderColor: "rgba(0, 0, 0, 0.15)" }}
                                                        whileFocus={{ scale: 1.01, borderColor: "rgba(227, 30, 36, 0.3)" }}
                                                        transition={{ type: "spring" as const, stiffness: 300, damping: 20 }}
                                                    >
                                                        <PhoneInput
                                                            name="phone"
                                                            defaultCountry="tr"
                                                            required
                                                            value={phoneValue}
                                                            onChange={(value) => setPhoneValue(value)}
                                                            placeholder={getVariable('contact_form.field.phone.placeholder', 'Enter your phone*')}
                                                            className="border border-[rgba(0,0,0,0.08)] focus-within:border-[rgba(0,0,0,0.40)] transition-all ease-in-out duration-300 rounded-lg !text-[#4F545A] font-medium text-sm sm:text-[15px] px-2 py-2"
                                                            inputClassName="!border-0 w-full"
                                                            countrySelectorStyleProps={{
                                                                buttonClassName: "!border-0 !px-2 !rounded-lg transition-all ease-in-out duration-300 hover:bg-gray-50",
                                                                dropdownStyleProps: {
                                                                    className: "!bg-white !border !border-[rgba(0,0,0,0.08)] !rounded-lg !shadow-tour-card !mt-1 !max-h-64 !overflow-y-auto",
                                                                    listItemClassName: "!px-3 !py-2 !hover:bg-alsero-soft-white !transition-colors !duration-200 !cursor-pointer !flex !items-center !gap-2",
                                                                    listItemFlagClassName: "!text-lg !mr-2",
                                                                    listItemCountryNameClassName: "!text-gray-700 !font-medium !text-sm",
                                                                    listItemDialCodeClassName: "!text-gray-500 !text-sm !ml-auto",
                                                                },
                                                            }}
                                                            aria-label={getVariable('contact_form.field.phone.aria_label', 'Enter your phone number')}
                                                        />
                                                    </motion.div>
                                                </div>
                                            </motion.div>
                                            <motion.div className="col-span-12" variants={formItemVariants} {...motionItemProps}>
                                                <div className="flex flex-col gap-5">
                                                    <label htmlFor="contact-message" className="font-semibold text-base sm:text-lg text-alsero-dark-gray">
                                                        {getVariable('contact_form.field.message.label', 'Message')}
                                                    </label>
                                                    <motion.textarea
                                                        id="contact-message"
                                                        name="message"
                                                        rows={8}
                                                        required
                                                        aria-required="true"
                                                        aria-label={getVariable('contact_form.field.message.aria_label', 'Enter your message')}
                                                        className="border border-[rgba(0,0,0,0.08)] focus:!border-[rgba(0,0,0,0.40)] transition-all ease-in-out duration-300 rounded-lg text-[#4F545A] font-medium text-sm sm:text-[15px] px-4 sm:px-6 py-3 sm:py-4"
                                                        placeholder={getVariable('contact_form.field.message.placeholder', 'Enter your message*')}
                                                        whileHover={{ scale: 1.005, borderColor: "rgba(0, 0, 0, 0.15)" }}
                                                        whileFocus={{ scale: 1.01, borderColor: "rgba(227, 30, 36, 0.3)" }}
                                                        transition={{ type: "spring" as const, stiffness: 300, damping: 20 }}
                                                    />
                                                </div>
                                            </motion.div>
                                            <motion.div className="col-span-12" variants={formItemVariants} {...motionItemProps}>
                           						<motion.div
                                                    className="flex items-center gap-3"
                                                    whileHover={{ x: 3 }}
                                                    transition={{ type: "spring" as const, stiffness: 300 }}
                                                >
                                                    <input type="checkbox" id="fallback-terms" className="hidden peer" required />
                                                    <label
                                                        htmlFor="fallback-terms"
                                                        className="cursor-pointer flex items-center gap-3 peer-checked:[&_div]:bg-alsero-first peer-checked:[&_div]:shadow-tour-card peer-checked:[&_div]:border-alsero-first peer-checked:[&_svg]:opacity-100"
                                                    >
                                                        <motion.div
                                                            className="bg-white w-6 h-6 p-1 flex justify-center items-center transition-colors duration-200 border border-[rgba(0,0,0,0.08)]  rounded-sm"
                                                            whileHover={{ scale: 1.1 }}
                                                            whileTap={{ scale: 0.9 }}
                                                        >
                                                            <svg className="stroke-white opacity-0 transition-opacity duration-200" viewBox="0 0 14 14" fill="none">
                                                                <path d="M3 8L6 11L11 3.5" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                                                            </svg>
                                                        </motion.div>
                                                        <span className="select-none text-alsero-dark-gray [&_a]:font-bold [&_a]:underline [&_a]:hover:text-alsero-first [&_a]:transition-all [&_a]:ease-in-out">
                                                            {(() => {
                                                                const text = getVariable('contact_form.agreement.fallback.terms', 'By signing up, you agree to {terms_link} and {privacy_link}');
                                                                const parts = text.split('{terms_link}');
                                                                if (parts.length === 2) {
                                                                    const [before, after] = parts;
                                                                    const privacyParts = after.split('{privacy_link}');
                                                                    if (privacyParts.length === 2) {
                                                                        return (
                                                                            <>
                                                                                {before}
                                                                                <Link href="#" target="_blank">Terms Of Service</Link>
                                                                                {privacyParts[0]}
                                                                                <Link href="#" target="_blank">Privacy Policy</Link>
                                                                                {privacyParts[1]}
                                                                            </>
                                                                        );
                                                                    }
                                                                }
                                                                return text;
                                                            })()}
                                                        </span>
                                                    </label>
                                                </motion.div>
                                            </motion.div>
                                        </>
                                    )
                                )}
                                {(requiredAgreements.length > 0 || optionalAgreements.length > 0) && (
                                    <>
                                        {requiredAgreements.length > 0 && (
                                            <motion.div
                                                className="col-span-12"
                                                variants={formItemVariants}
                                                {...motionItemProps}
                                            >
                                                <div className="flex flex-col gap-2 text-alsero-dark-gray text-sm">
                                                    {requiredAgreements.map((agreement) => (
                                                        <div key={agreement.id}>
                                                            {renderAgreementLabel(agreement)}
                                                        </div>
                                                    ))}
                                                </div>
                                            </motion.div>
                                        )}
                                        {optionalAgreements.map((agreement) => (
                                            <motion.div
                                                key={agreement.id}
                                                className="col-span-12"
                                                variants={formItemVariants}
                                                {...motionItemProps}
                                            >
                                                <motion.div
                                                    className="flex items-center gap-3"
                                                    whileHover={{ x: 3 }}
                                                    transition={{ type: "spring" as const, stiffness: 300 }}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        id={`agreement-${agreement.id}`}
                                                        className="hidden peer"
                                                        checked={agreementsState[agreement.id] || false}
                                                        onChange={(event) => {
                                                            const checked = event.target.checked;
                                                            setAgreementsState((prev) => ({
                                                                ...prev,
                                                                [agreement.id]: checked,
                                                            }));
                                                        }}
                                                    />
                                                    <label
                                                        htmlFor={`agreement-${agreement.id}`}
                                                        className="cursor-pointer flex items-center gap-3 peer-checked:[&_div]:bg-alsero-first peer-checked:[&_div]:shadow-tour-card peer-checked:[&_div]:border-alsero-first peer-checked:[&_svg]:opacity-100"
                                                    >
                                                        <motion.div
                                                            className="bg-white w-6 h-6 p-1 flex justify-center items-center transition-colors duration-200 border border-[rgba(0,0,0,0.08)] rounded-sm"
                                                            whileHover={{ scale: 1.1 }}
                                                            whileTap={{ scale: 0.9 }}
                                                        >
                                                            <svg className="stroke-white opacity-0 transition-opacity duration-200" viewBox="0 0 14 14" fill="none">
                                                                <path d="M3 8L6 11L11 3.5" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                                                            </svg>
                                                        </motion.div>
                                                        <span className="select-none text-alsero-dark-gray [&_button]:font-bold [&_button]:underline [&_button]:hover:text-alsero-first [&_button]:transition-all [&_button]:ease-in-out">
                                                            {renderAgreementLabel(agreement)}
                                                        </span>
                                                    </label>
                                                </motion.div>
                                            </motion.div>
                                        ))}
                                    </>
                                )}
                                
                                {/* Captcha Widget - Provider'a göre göster */}
                                {captchaConfigLoading ? (
                                    <motion.div
                                        className="col-span-12 flex justify-center items-center h-24"
                                        variants={formItemVariants}
                                        {...motionItemProps}
                                    >
                                        <span className="spinner-border spinner-border-sm align-middle me-2"></span>
                                        {getVariable('contact_form.captcha_loading', 'Captcha yükleniyor...')}
                                    </motion.div>
                                ) : captchaConfigError ? (
                                    <motion.div
                                        className="col-span-12"
                                        variants={formItemVariants}
                                        {...motionItemProps}
                                    >
                                        <div className="text-sm text-yellow-600">
                                            {getVariable('contact_form.captcha_error', 'Captcha yapılandırması yüklenemedi.')}
                                        </div>
                                    </motion.div>
                                ) : captchaConfig && captchaConfig.is_enabled && captchaConfig.site_key && captchaConfig.provider ? (
                                    <>
                                        {captchaConfig.provider === 'google_recaptcha_v2' && (
                                            <motion.div
                                                className="col-span-12"
                                                variants={formItemVariants}
                                                {...motionItemProps}
                                            >
                                                <div className="flex justify-start">
                                                    <div 
                                                        ref={captchaContainerRef}
                                                        id="contact-captcha-widget-container"
                                                        className="g-recaptcha"
                                                        style={{ minHeight: '78px' }}
                                                    />
                                                </div>
                                            </motion.div>
                                        )}
                                        {captchaConfig.provider === 'cloudflare_turnstile' && (
                                            <motion.div
                                                className="col-span-12"
                                                variants={formItemVariants}
                                                {...motionItemProps}
                                            >
                                                <div className="flex justify-start">
                                                    <div 
                                                        ref={captchaContainerRef}
                                                        id="contact-captcha-widget-container"
                                                        style={{ minHeight: '65px' }}
                                                    />
                                                </div>
                                            </motion.div>
                                        )}
                                        {(captchaConfig.provider === 'google_recaptcha_v3' || captchaConfig.provider === 'google_recaptcha_enterprise') && (
                                            <motion.div
                                                className="col-span-12"
                                                variants={formItemVariants}
                                                {...motionItemProps}
                                            >
                                                <div className="text-sm text-gray-500 mb-2">
                                                    {getVariable('common.captcha_auto_note', 'Robot doğrulaması gönder sırasında otomatik yapılır.')}
                                                </div>
                                            </motion.div>
                                        )}
                                    </>
                                ) : null}
                                
                                {submitStatus.type && (
                                    <motion.div
                                        className="col-span-12"
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                    >
                                        <div
                                            className={`p-4 rounded-lg ${
                                                submitStatus.type === 'success'
                                                    ? 'bg-green-50 text-green-800 border border-green-200'
                                                    : 'bg-red-50 text-red-800 border border-red-200'
                                            }`}
                                        >
                                            {submitStatus.message}
                                        </div>
                                    </motion.div>
                                )}
                                <motion.div className="col-span-12 flex items-start gap-2" variants={formItemVariants}>
                                    <input
                                        type="checkbox"
                                        id="contact-form-newsletter"
                                        checked={newsletterSubscribed}
                                        onChange={(e) => setNewsletterSubscribed(e.target.checked)}
                                        className="mt-0.5 h-4 w-4 text-alsero-first focus:ring-alsero-first border-gray-300 rounded cursor-pointer"
                                        aria-describedby="contact-form-newsletter-label"
                                    />
                                    <label id="contact-form-newsletter-label" htmlFor="contact-form-newsletter" className="text-sm text-alsero-dark-gray cursor-pointer">
                                        {getVariable('form.newsletter_subscribe_label', 'I would like to subscribe to the newsletter. You can check this to receive campaigns and news by email.')}
                                    </label>
                                </motion.div>
                                <motion.div
                                    className="col-span-12"
                                    variants={formItemVariants}
                                >
                                    <motion.button
                                        type="submit"
                                        disabled={submitting || captchaConfigLoading}
                                        className="bg-alsero-dark-gray px-6 sm:px-8 md:px-10 py-3 sm:py-4 md:py-5 rounded-lg cursor-pointer transition-all ease-in-out duration-300 hover:shadow-lg text-white font-semibold text-sm sm:text-base md:text-lg uppercase disabled:opacity-50 disabled:cursor-not-allowed"
                                        variants={buttonVariants}
                                        initial="rest"
                                        whileHover={submitting ? "rest" : "hover"}
                                        whileTap={submitting ? "rest" : "tap"}
                                        aria-label={getVariable('contact_form.button.aria_label', 'Send contact form message')}
                                    >
                                        {submitting ? getVariable('contact_form.button.submitting', 'Gönderiliyor...') : getVariable('contact_form.button.send', 'Send message')}
                                    </motion.button>
                                </motion.div>
                            </div>
                        </motion.form>
                    </motion.div>
                </div>
            </div>
            {/* Agreement Modal */}
            <AgreementModal
                isOpen={modalOpen}
                onClose={() => {
                    setModalOpen(false);
                    setSelectedAgreementSlug(null);
                    setSelectedAgreementId(undefined);
                    setSelectedAgreementIsRequired(false);
                }}
                agreementSlug={selectedAgreementSlug}
                language={language}
                agreementId={selectedAgreementId}
                isRequired={selectedAgreementIsRequired}
                onAccept={(agreementId) => {
                    // Seçmeli sözleşme için checkbox'ı işaretle
                    setAgreementsState((prev) => ({
                        ...prev,
                        [agreementId]: true,
                    }));
                }}
            />
        </motion.section>
    )
}